/**
 * In-Page Links Transformer
 * Transforms WooRank links criteria data into React component-ready format
 */

import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
} from '../../constants/seoMappings';

/**
 * Calculates link statistics from the links data
 * @param {Array} data - The links data array
 * @returns {Object} - Statistics with counts and percentages
 */
const calculateLinkStats = (data = []) => {
  if (!data || data?.length === 0) {
    return {
      totalLinks: 0,
      linksToFiles: 0,
      internalFollow: { count: 0, percentage: 0 },
      internalNoFollow: { count: 0, percentage: 0 },
      externalFollow: { count: 0, percentage: 0 },
      externalNoFollow: { count: 0, percentage: 0 },
    };
  }

  const totalLinks = data?.length || 0;
  let linksToFiles = 0;
  let internalFollow = 0;
  let internalNoFollow = 0;
  let externalFollow = 0;
  let externalNoFollow = 0;

  (data || []).forEach((link = {}) => {
    // Count links to files
    // API attribute: linkToFile (boolean)
    const linkToFileValue = link?.linkToFile ?? false;
    if (linkToFileValue === true) {
      linksToFiles++;
    }

    // Categorize by internal/external and follow/nofollow
    // API attributes: internal (boolean), follow (boolean)
    const isInternal = link?.internal ?? false;
    const isFollow = link?.follow ?? false;

    if (isFollow && isInternal) {
      internalFollow++;
    } else if (isFollow) {
      externalFollow++;
    } else if (isInternal) {
      internalNoFollow++;
    } else {
      externalNoFollow++;
    }
  });

  // Calculate percentages using Largest Remainder Method to ensure they sum to 100%
  const calculatePercentagesLargestRemainder = (counts) => {
    if (totalLinks === 0) {
      return counts.map(() => 0);
    }

    // Calculate exact percentages and their floored values
    const exactPercentages = counts.map((count) => (count / totalLinks) * 100);
    const flooredPercentages = exactPercentages.map((p) => Math.floor(p));
    const remainders = exactPercentages.map((p, i) => ({ index: i, remainder: p - flooredPercentages[i] }));

    // Calculate how many percentage points we need to distribute
    const flooredSum = flooredPercentages.reduce((sum, p) => sum + p, 0);
    let pointsToDistribute = 100 - flooredSum;

    // Sort by largest remainder and distribute extra points
    remainders.sort((a, b) => b.remainder - a.remainder);
    const result = [...flooredPercentages];
    for (let i = 0; i < pointsToDistribute && i < remainders.length; i++) {
      if (remainders[i].remainder > 0) {
        result[remainders[i].index]++;
      }
    }

    return result;
  };

  const counts = [internalFollow, internalNoFollow, externalFollow, externalNoFollow];
  const percentages = calculatePercentagesLargestRemainder(counts) || [0, 0, 0, 0];
  const intFollowPct = percentages?.[0] || 0;
  const intNoFollowPct = percentages?.[1] || 0;
  const extFollowPct = percentages?.[2] || 0;
  const extNoFollowPct = percentages?.[3] || 0;

  return {
    totalLinks: totalLinks || 0,
    linksToFiles: linksToFiles || 0,
    internalFollow: { count: internalFollow || 0, percentage: intFollowPct || 0 },
    internalNoFollow: { count: internalNoFollow || 0, percentage: intNoFollowPct || 0 },
    externalFollow: { count: externalFollow || 0, percentage: extFollowPct || 0 },
    externalNoFollow: { count: externalNoFollow || 0, percentage: extNoFollowPct || 0 },
  };
};

/**
 * Transforms raw link data into display-ready format
 * @param {Array} data - The links data array
 * @returns {Array} - Flattened array of links for table display
 */
const transformLinksForDisplay = (data = []) => {
  if (!data || data?.length === 0) {
    return [];
  }

  return (data || []).map((link = {}) => {
    // API attribute: hypertext (string) - anchor text
    const hypertextValue = link?.hypertext ?? '';
    const anchor = hypertextValue && hypertextValue?.trim() !== ''
      ? hypertextValue
      : 'No Anchor Text';

    // API attribute: internal (boolean)
    const isInternal = link?.internal ?? false;
    const type = isInternal ? 'Internal Link' : 'External Link';

    // API attribute: follow (boolean)
    const isFollow = link?.follow ?? false;
    const followText = isFollow ? 'Follow' : 'Not Follow';

    // API attribute: linkToFile (boolean)
    const isFile = link?.linkToFile ?? false;

    return {
      anchor: anchor || 'No Anchor Text',
      url: link?.link ?? '',
      type: type || 'External Link',
      follow: followText || 'Not Follow',
      isFile: isFile === true,
    };
  });
};

/**
 * Generates a scenario-based message for in-page links
 * @param {Object} stats - The link statistics
 * @returns {string} - The appropriate message based on the scenario
 */
const generateMessage = (stats = {}) => {
  const totalLinks = stats?.totalLinks || 0;
  const linksToFiles = stats?.linksToFiles || 0;

  // No links found
  if (totalLinks === 0) {
    return 'No links were found on this page.';
  }

  // Links exist
  return `We found a total of ${totalLinks} link${totalLinks === 1 ? '' : 's'} including ${linksToFiles} link${linksToFiles === 1 ? '' : 's'} to files.`;
};

/**
 * Transforms the links criteria from WooRank API response
 * @param {Object|Array} linksCriteria - The links criteria object or array from the API
 * @returns {Object} - Transformed in-page links data for React components
 */
export const transformInPageLinks = (linksCriteria) => {
  // Handle missing criteria entirely
  if (!linksCriteria) {
    return {
      status: 'error',
      iconType: 'error',
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: 'In-page links data is unavailable.',
      data: null,
    };
  }

  // Handle both formats:
  // 1. Array directly (old format): linksCriteria = [...]
  // 2. Object with data property (new format): linksCriteria = { data: [...], status, importance, solvability }
  const isArray = Array.isArray(linksCriteria);
  const data = isArray ? linksCriteria : (linksCriteria?.data || []);
  const wooRankStatus = isArray ? 'neutral' : (linksCriteria?.status || 'neutral');
  const importance = isArray ? 0 : (linksCriteria?.importance || 0);
  const solvability = isArray ? 0 : (linksCriteria?.solvability || 0);

  // Calculate link statistics
  const stats = calculateLinkStats(data) || {};

  // Check if data is empty (no links)
  const isEmpty = (stats?.totalLinks || 0) === 0;

  // Use API status directly - special scenarios only affect messages
  const status = getStatus(wooRankStatus);
  const iconType = getIconType(wooRankStatus);

  return {
    status,
    iconType,
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    message: generateMessage(stats),
    data: {
      totalLinks: stats?.totalLinks || 0,
      linksToFiles: stats?.linksToFiles || 0,
      internalFollow: stats?.internalFollow || { count: 0, percentage: 0 },
      internalNoFollow: stats?.internalNoFollow || { count: 0, percentage: 0 },
      externalFollow: stats?.externalFollow || { count: 0, percentage: 0 },
      externalNoFollow: stats?.externalNoFollow || { count: 0, percentage: 0 },
      links: transformLinksForDisplay(data) || [],
    },
  };
};

export default transformInPageLinks;

